import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ApiError, api } from '../../api/client.js';
import { ErrorState, Field, Spinner, StatusBadge } from '../../components/ui.jsx';
import { StatTile } from '../../components/charts.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatDate, formatXaf } from '../../lib/format.js';

const monthKey = (date) => date.toISOString().slice(0, 7);

const monthLabel = (iso) =>
  new Date(`${iso}-01T12:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const addDays = (iso, days) => {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/**
 * The month's round: every customer on one meter, on one sheet.
 *
 * A landlord reads a compound of meters in one walk and then wants to enter what
 * they wrote down, not open a page per person. The arithmetic stays on the
 * server - each row's total is worked out from the same figures the bill will
 * carry - and a reading that will not do is reported against its own row so one
 * bad number never costs the whole round.
 */
const MonthlyBilling = () => {
  const { id } = useParams();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const month = params.get('month') || monthKey(new Date());

  const run = useFetch(`/landlord/utility-services/${id}/billing-run?month=${month}-01`, [id, month]);
  const [readings, setReadings] = useState({});
  // Corrections to where a dial started, for meters nothing has been billed from
  // yet. Keyed by account, like the readings.
  const [openings, setOpenings] = useState({});
  const [dueDate, setDueDate] = useState('');
  const [errors, setErrors] = useState({});
  const [failures, setFailures] = useState([]);
  const [busy, setBusy] = useState(false);

  useTitle(run.data ? `${run.data.service.name} - ${monthLabel(month)}` : 'Monthly billing');

  // A fresh month means fresh readings and a deadline a week out from the end of
  // it, which is what a landlord would have written on the paper version.
  useEffect(() => {
    setReadings({});
    setOpenings({});
    setFailures([]);
    setErrors({});
    if (run.data) setDueDate(addDays(run.data.period.end, 7));
  }, [run.data?.period.end]);

  const metered = run.data?.service.billingMode === 'metered';

  // The sheet previews a whole meter at once, so it does the sum here rather
  // than asking the server per row. The server remains the authority - see
  // calculateBill in server/src/lib/utilities.js - and this must round the same
  // way it does, or the total promised here is not the total billed.
  const roundUp = (amount) => Math.ceil(amount / 100) * 100;

  const rows = useMemo(() => {
    if (!run.data) return [];
    return run.data.accounts.map((account) => {
      const typed = readings[account.id];
      const current = typed === undefined || typed === '' ? null : Number(typed);
      // The figure the sheet counts up from: what they have corrected it to, or
      // what the dial last read.
      const correction = openings[account.id];
      const previous =
        correction === undefined || correction === '' ? account.previousReading : Number(correction);
      const units = current === null ? null : Math.round((current - previous) * 1000) / 1000;
      const exact =
        !metered
          ? run.data.service.flatAmount + run.data.service.standingCharge
          : units === null || units < 0
            ? null
            : Math.round(units * run.data.service.unitCost) + run.data.service.standingCharge;
      const amount = exact === null ? null : roundUp(exact);

      return { account, current, previous, units, exact, amount, backwards: units !== null && units < 0 };
    });
  }, [run.data, readings, openings, metered]);

  const toBill = rows.filter((row) => !row.account.existingBill && (!metered || row.current !== null));
  const total = toBill.reduce((sum, row) => sum + (row.amount || 0), 0);
  const anyBackwards = toBill.some((row) => row.backwards);

  if (run.loading && !run.data) return <Spinner />;
  if (run.error) {
    return (
      <div className="container section">
        <ErrorState message={run.error} onRetry={run.reload} />
      </div>
    );
  }

  const { service, period, accounts } = run.data;
  const drafts = accounts.filter((account) => account.existingBill && !account.existingBill.posted);

  const billTheRound = async () => {
    setBusy(true);
    setErrors({});
    setFailures([]);
    try {
      const result = await api.post(`/landlord/utility-services/${id}/billing-run`, {
        periodStart: period.start,
        periodEnd: period.end,
        dueDate,
        readings: toBill.map((row) => ({
          accountId: row.account.id,
          ...(metered ? { currentReading: row.current } : {}),
          // Only when they actually changed it, and only for a meter that has
          // never been billed - the server refuses it otherwise.
          ...(metered && !row.account.billed && openings[row.account.id] !== undefined && openings[row.account.id] !== ''
            ? { openingReading: Number(openings[row.account.id]) }
            : {})
        }))
      });

      setFailures(result.failed || []);
      if (result.created.length > 0) {
        toast.success(
          `${result.created.length} ${result.created.length === 1 ? 'bill' : 'bills'} drawn up as drafts. ` +
            'Check them, then send them out.'
        );
      }
      if (result.failed.length > 0) toast.error(`${result.failed.length} could not be billed. See the notes below.`);
      setReadings({});
      setOpenings({});
      run.reload();
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const sendTheRound = async () => {
    setBusy(true);
    try {
      const result = await api.post(`/landlord/utility-services/${id}/post-drafts`, { periodStart: period.start });
      toast.success(
        result.posted.length > 0
          ? `${result.posted.length} sent. Each customer has the reading, the total and the deadline.`
          : 'Nothing was waiting to be sent.'
      );
      if (result.failed.length > 0) {
        setFailures(result.failed);
        toast.error(
          `${result.failed.length} held back. ${
            result.failed.some((row) => /photograph/i.test(row.error))
              ? 'A reading cannot be sent without a photograph of the dial.'
              : 'See the notes below.'
          }`
        );
      }
      run.reload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>{monthLabel(month)}</h1>
          <p className="muted">
            {service.name} - everybody on the meter, on one sheet.{' '}
            {metered
              ? `Charged at ${formatXaf(service.unitCost)} per ${service.unitLabel}.`
              : `Charged at ${formatXaf(service.flatAmount)} each month.`}
          </p>
        </div>
        <div className="row-actions">
          <input
            type="month"
            className="month-picker"
            value={month}
            onChange={(event) => setParams({ month: event.target.value })}
          />
          <Link to={`/landlord/utilities/${id}`} className="btn btn-ghost">
            Back to the meter
          </Link>
        </div>
      </header>

      <div className="stat-row">
        <StatTile value={accounts.length} label="On this meter" />
        <StatTile value={accounts.filter((a) => a.existingBill).length} label="Already billed this month" />
        <StatTile value={toBill.length} label="Ready to bill" />
        <StatTile value={formatXaf(total)} label="Comes to" note="Before anything is saved" />
      </div>

      {failures.length > 0 ? (
        <div className="notice notice-bad">
          <strong>These could not be billed:</strong>
          <ul className="plain-list">
            {failures.map((failure, index) => (
              <li key={failure.accountId ?? failure.reference ?? index}>
                {failure.customerName || failure.reference} - {failure.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="table-wrap">
        <table className="data-table billing-sheet">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Meter</th>
              {metered ? <th>Last read</th> : null}
              {metered ? <th>Reading now</th> : null}
              <th>Comes to</th>
              <th>This month</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 ? (
              <tr>
                <td colSpan={metered ? 6 : 4} className="muted">
                  Nobody is on this meter yet.
                </td>
              </tr>
            ) : null}

            {rows.map(({ account, units, exact, amount, backwards }) => (
              <tr key={account.id} className={account.existingBill ? 'is-done' : ''}>
                <td data-label="Customer">
                  <div className="cell-stack">
                    <Link to={`/landlord/utilities/accounts/${account.id}`}>{account.customer?.name}</Link>
                    <span className="muted">{account.isTenant ? 'Tenant' : 'Not a tenant'}</span>
                  </div>
                </td>
                <td data-label="Meter">{account.meterNumber || <span className="muted">-</span>}</td>

                {/* Where the dial started stays the landlord's to correct until
                    a bill has been raised from it. After that it is the figure
                    the last bill ended on, and moving it would rewrite what has
                    already been paid. */}
                {metered ? (
                  <td data-label="Last read">
                    {account.billed ? (
                      (account.previousReading ?? 0)
                    ) : (
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        className="opening-cell"
                        // ?? 0 and not String(undefined): a figure that failed
                        // to arrive would otherwise render as an empty box,
                        // which reads as "this meter has no last reading" when
                        // it means "something did not load".
                        value={openings[account.id] ?? String(account.previousReading ?? 0)}
                        onChange={(event) =>
                          setOpenings((current) => ({ ...current, [account.id]: event.target.value }))
                        }
                        aria-label={`Last reading for ${account.customer?.name}`}
                      />
                    )}
                  </td>
                ) : null}

                {metered ? (
                  <td data-label="Reading now">
                    {account.existingBill ? (
                      <span className="muted">{account.existingBill.currentReading}</span>
                    ) : (
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        className={backwards ? 'is-wrong' : ''}
                        value={readings[account.id] ?? ''}
                        onChange={(event) =>
                          setReadings((current) => ({ ...current, [account.id]: event.target.value }))
                        }
                        aria-label={`Reading now for ${account.customer?.name}`}
                      />
                    )}
                  </td>
                ) : null}

                <td data-label="Comes to">
                  {account.existingBill ? (
                    <strong>{formatXaf(account.existingBill.amount)}</strong>
                  ) : backwards ? (
                    <span className="overdue-flag">the dial has gone backwards</span>
                  ) : amount === null ? (
                    <span className="muted">-</span>
                  ) : (
                    <div className="cell-stack">
                      <strong>{formatXaf(amount)}</strong>
                      {metered ? (
                        <span className="muted">
                          {units} {service.unitLabel} x {formatXaf(service.unitCost)}
                          {service.standingCharge > 0 ? ` + ${formatXaf(service.standingCharge)}` : ''}
                        </span>
                      ) : null}
                      {amount !== exact ? (
                        <span className="muted">{formatXaf(exact)}, rounded up</span>
                      ) : null}
                    </div>
                  )}
                </td>

                <td data-label="This month">
                  {account.existingBill ? (
                    <StatusBadge
                      status={account.existingBill.posted ? 'approved' : 'draft'}
                      label={account.existingBill.posted ? 'Sent' : 'Draft'}
                    />
                  ) : (
                    <span className="muted">Not billed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section className="section-block">
        <div className="grid-2">
          <Field
            label="Everybody pays by"
            hint="The service is cut off two days after this if a bill is not paid."
            error={errors.dueDate}
          >
            <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
          </Field>
        </div>

        <div className="row-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={billTheRound}
            disabled={busy || toBill.length === 0 || anyBackwards || !dueDate}
          >
            {busy
              ? 'Working...'
              : `Draw up ${toBill.length} ${toBill.length === 1 ? 'bill' : 'bills'} (${formatXaf(total)})`}
          </button>

          {drafts.length > 0 ? (
            <button type="button" className="btn btn-outline" onClick={sendTheRound} disabled={busy}>
              Send {drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'} to customers
            </button>
          ) : null}
        </div>

        <p className="field-hint">
          Bills are drawn up as drafts first. Nothing reaches a customer, and no deadline runs, until you send them.
          {period.start ? ` Period ${formatDate(period.start)} to ${formatDate(period.end)}.` : ''}
        </p>
      </section>
    </div>
  );
};

export default MonthlyBilling;
